// Auto-generated by splitter.py — do not regenerate; edit directly.

import { countNoun } from '../../lib/format';
import React, { useState, useEffect } from 'react';

const CAMPAIGN_STEPS = [
  { id: 'channel',  label: '1. Channel' },
  { id: 'audience', label: '2. Audience' },
  { id: 'compose',  label: '3. Compose' },
  { id: 'review',   label: '4. Review & send' },
];
import { ChevronLeft, Download, Hash, Loader2, Mail, MessageSquare, Send, Tag, Upload, Users, CalendarDays } from 'lucide-react';
import { createCampaign, loadMailTemplates, loadMailTemplate, previewCampaignAudience, loadAudienceTags, loadContactLists, importContactListCsv } from '../../lib/backOfficeApi';
import { logAudit, toast } from '../../lib/buses';
import { EmailTest, MailPreview, TemplateCheck } from './MailTemplatesPanel';
import { checkMailTemplate, fillSample, SAMPLE_RECIPIENT } from './mailTemplate';
import { Field, Input, Textarea, Select } from '../../ui/Form';
import { Modal } from '../../ui/Modal';
import { EventSelect } from '../../components/filters/EventSelect';
import { WhatsAppTemplatePicker, templateEditableFields } from './WhatsAppTemplatePicker';
import { AudienceBreakdown } from './AudienceBreakdown';
import {
  DirectSendComposer, DirectSendTest, UnfinishedCheckoutsPicker, WhatsAppBubble,
  defaultUnfinishedWindow, renderWithSample, windowToInstants,
} from './DirectSend';

// Audiences a WRITTEN WhatsApp message may go to — each person has a transaction of their own for it
// to be about. The backend enforces the same list (DIRECT_SEND_AUDIENCES); this only decides what
// the composer offers.
const DIRECT_SEND_AUDIENCES = ['buyers', 'unfinished', 'accreditation'];

// Shows the REAL resolved audience size from the backend preview (loading / number /
// error) — never a fabricated estimate. `exact:false` (multi-source overlap) shows ≈.
const AudienceSizeBar = ({ resolved, idle, idleHint }) => {
  if (idle) return <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md px-4 py-2.5 text-[11.5px] text-[#9CA3AF]">{idleHint}</div>;
  return (
    <div className="bg-white border border-[#1F2937] rounded-md px-4 py-2.5 flex items-center justify-between">
      <div className="text-[11.5px] text-[#6B7280]">Audience size</div>
      <div className="text-right">
        {resolved.status === 'loading' && <div className="text-[13px] text-[#6B7280] inline-flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" />Resolving…</div>}
        {resolved.status === 'error' && <div className="text-[12px] text-[#9B1F1F] max-w-[360px]">{resolved.error || 'Could not resolve size'}</div>}
        {resolved.status === 'ready' && <>
          <div className="text-[16px] font-semibold tabular-nums text-[#1F2937]">{resolved.exact ? '' : '≈'}{resolved.total.toLocaleString()}</div>
          <div className="text-[10px] text-[#9CA3AF]">recipients · live from real data</div>
        </>}
      </div>
    </div>
  );
};

export const NewCampaignModal = ({ designs, onClose, onSend }) => {
  const [step, setStep] = useState('channel'); // channel | audience | compose | review
  const [channel, setChannel] = useState('email');
  const [audienceSource, setAudienceSource] = useState('event'); // event | segment | upload
  // Event audience (real)
  const [picked, setPicked] = useState({}); // events picked in this modal, by id — the pickers search; nothing preloaded
  const pick = (e) => e && setPicked((p) => ({ ...p, [e.id]: e }));
  const [eventId, setEventId] = useState('');
  // The event the campaign is FOR — not always the audience's: a B'raka Land email to the people
  // who went to Etania is B'raka Land's. Empty = the audience's own event.
  const [forEventId, setForEventId] = useState('');
  const [audienceType, setAudienceType] = useState('buyers'); // buyers | subscribers | all
  // Saved segment = real subscriber tags
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState('');
  // Contact lists — an existing one, or a CSV uploaded here that becomes one.
  const [lists, setLists] = useState([]);
  const [listId, setListId] = useState('');
  const [upload, setUpload] = useState({ name: '', eventId: '', busy: false, result: null, error: '' });
  // People another campaign reached in the last 7 days are left out unless the operator says otherwise.
  const [includeRecent, setIncludeRecent] = useState(false);
  // Real resolved audience size from the backend
  const [resolved, setResolved] = useState({ status: 'idle', total: 0, exact: true });
  const [name, setName] = useState('');
  // An email's body is a named mail template (Design & Comms → Campaign emails); the campaign
  // carries its name. The subject is the template's too — the mail service sends the template's.
  const [mailTpls, setMailTpls] = useState({ state: 'idle', templates: [], variables: [], sender: null, error: '' });
  const [mailTpl, setMailTpl] = useState(null); // { name, subject, html }
  const [fromName, setFromName] = useState('');
  const [body, setBody] = useState('');
  // WhatsApp is TEMPLATE-only for a business-initiated message (Meta's 24h rule), so a
  // WhatsApp campaign carries an approved template + its parameters, not prose.
  const [waTemplate, setWaTemplate] = useState(null); // { name, languageCode, components }
  // ...or a WRITTEN one (WhatsApp Direct Send, utility) for audiences with a transaction to be about.
  const [waMode, setWaMode] = useState('template'); // template | direct
  const [direct, setDirect] = useState({ body: '', buttonText: 'Get tickets', withButton: true, save: false, label: '', startedFrom: null });
  // Unfinished checkouts: the incident window and which outcomes to include (null = backend default).
  const [unfinishedWindow, setUnfinishedWindow] = useState(defaultUnfinishedWindow);
  const [unfinishedOutcomes, setUnfinishedOutcomes] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  // People per click. Null = the audience's own default (the preview says what that is).
  const [batchSize, setBatchSize] = useState(null);

  const channelLabel = channel === 'email' ? 'Email' : channel === 'whatsapp' ? 'WhatsApp' : 'SMS';
  const evName = (id) => { const e = picked[id]; return e?.name || e?.title || id; };

  // Load real subscriber tags and contact lists once.
  useEffect(() => {
    const ac = new AbortController();
    loadAudienceTags({ signal: ac.signal }).then((t) => { if (!ac.signal.aborted) setTags(t || []); }).catch(() => {});
    loadContactLists({ signal: ac.signal }).then((l) => { if (!ac.signal.aborted) setLists(l || []); }).catch(() => {});
    return () => ac.abort();
  }, []);

  useEffect(() => {
    if (channel !== 'email' || mailTpls.state !== 'idle') return;
    setMailTpls((m) => ({ ...m, state: 'loading' }));
    loadMailTemplates().then(({ templates, variables, sender }) => setMailTpls({ state: 'ready', templates, variables, sender, error: '' }))
      .catch((e) => setMailTpls({ state: 'error', templates: [], variables: [], error: e.message }));
  }, [channel, mailTpls.state]);
  const pickMailTemplate = (name) => {
    if (!name) { setMailTpl(null); return; }
    setMailTpl({ name, subject: '', html: '', loading: true });
    loadMailTemplate(name).then((t) => setMailTpl({ name: t.name, subject: t.subject, html: t.html }))
      .catch((e) => { setMailTpl(null); toast('Could not open the template', 'error', e.message); });
  };

  // The real audienceSources for the current selection (mirror the backend resolver).
  const audienceSources = (() => {
    if (audienceSource === 'event' && eventId && audienceType === 'unfinished') {
      return [{ type: 'event', sourceId: eventId, audienceType, sourceName: evName(eventId),
        ...windowToInstants(unfinishedWindow), ...(unfinishedOutcomes ? { outcomes: unfinishedOutcomes } : {}) }];
    }
    if (audienceSource === 'event' && eventId) return [{ type: 'event', sourceId: eventId, audienceType, sourceName: evName(eventId) }];
    if (audienceSource === 'segment' && selectedTag) return [{ type: 'tag', sourceId: selectedTag, sourceName: selectedTag }];
    if (audienceSource === 'list' && listId) return [{ type: 'contactList', sourceId: listId, sourceName: lists.find((l) => l.id === listId)?.name || 'Contact list' }];
    return [];
  })();

  const directAllowed = channel === 'whatsapp' && audienceSource === 'event' && DIRECT_SEND_AUDIENCES.includes(audienceType);
  const isDirect = channel === 'whatsapp' && waMode === 'direct' && directAllowed;
  // The wording, as the backend reads it: the preview uses it to count out people who already got these exact words.
  const directContent = isDirect && direct.body.trim()
    ? { body: direct.body, buttonText: direct.withButton ? (direct.buttonText || null) : null } : null;

  // An unfinished-checkout audience is what a written message is for; anything a written message
  // may not go to puts the composer back on templates.
  useEffect(() => {
    if (audienceType === 'unfinished' && channel !== 'whatsapp') { setAudienceType('buyers'); return; }
    if (audienceType === 'unfinished') setWaMode('direct');
    else if (!directAllowed) setWaMode('template');
  }, [audienceType, directAllowed, channel]);

  // Resolve the REAL audience size from the backend whenever the selection changes.
  useEffect(() => {
    if (!audienceSources.length) { setResolved({ status: 'idle', total: 0, exact: true }); return; }
    let alive = true;
    setResolved((r) => ({ ...r, status: 'loading' }));
    const t = setTimeout(() => {
      previewCampaignAudience(audienceSources, { channel, content: directContent, batchSize, includeRecent, eventId: forEventId || undefined })
        .then((d) => { if (alive) setResolved({ status: 'ready', total: d.total || 0, exact: d.exact !== false, detail: d.perSource?.[0] || null }); })
        .catch((e) => { if (alive) setResolved({ status: 'error', total: 0, exact: true, error: e?.message }); });
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [audienceSource, eventId, forEventId, audienceType, selectedTag, listId, channel, batchSize, includeRecent, JSON.stringify(unfinishedWindow), JSON.stringify(unfinishedOutcomes), JSON.stringify(directContent)]); // eslint-disable-line

  const audience = {
    label: audienceSource === 'event' ? `${evName(eventId)} · ${audienceType === 'unfinished' ? 'unfinished checkouts' : audienceType}` : audienceSource === 'segment' ? `Segment: ${selectedTag}` : `List: ${lists.find((l) => l.id === listId)?.name || '—'}`,
    sources: audienceSources,
  };
  const audienceSize = resolved.total || 0;
  const hasValidAudience = audienceSources.length > 0 && resolved.status === 'ready' && audienceSize > 0;
  // A list's campaign is its event's (and so its organiser's) — the same owner the list has.
  const listEventId = audienceSource === 'list' ? (lists.find((l) => l.id === listId)?.eventId || '') : '';
  const audienceEventId = (audienceSource === 'event' && eventId) || listEventId || '';
  const campaignEventId = forEventId || audienceEventId;
  // The preview's recipient: the audience's own first person when the count has one.
  const emailSample = {
    ...SAMPLE_RECIPIENT,
    ...(resolved.detail?.sample?.firstName ? { firstName: resolved.detail.sample.firstName, name: resolved.detail.sample.firstName } : {}),
    ...(campaignEventId ? { event_name: evName(campaignEventId) } : {}),
  };

  const canCompose = hasValidAudience && name.trim();
  // WhatsApp's payload is the TEMPLATE, not `body` — gating it on prose would have blocked
  // Review on a campaign that is complete, and (before the picker existed) let one through
  // with no template at all.
  const canReview = canCompose && (channel === 'whatsapp'
    ? (isDirect
      ? !!direct.body.trim() && direct.body.length <= 1024
        && (!direct.withButton || !!direct.buttonText.trim())
        && (!direct.save || !!direct.label.trim())
      : !!waTemplate?.name)
    : channel === 'email' ? !!(mailTpl?.html && !mailTpl.loading)
    : !!body.trim());
  const reviewReason = canReview ? null
    : !audienceSources.length ? 'Pick an audience'
    : resolved.status === 'loading' ? 'Still counting the audience'
    : resolved.status === 'error' ? "Couldn't count the audience"
    : resolved.status !== 'ready' || audienceSize <= 0 ? 'The audience has no one to send to'
    : !name.trim() ? 'Name the campaign'
    : channel === 'whatsapp'
      ? (isDirect
        ? (!direct.body.trim() ? 'Write the message' : direct.body.length > 1024 ? 'Keep the message to 1,024 characters'
          : direct.withButton && !direct.buttonText.trim() ? 'Enter the button text'
          : direct.save && !direct.label.trim() ? 'Name the saved message' : null)
        : 'Pick a WhatsApp template')
      : channel === 'email' ? (mailTpl?.loading ? 'Opening the template' : 'Pick the email to send')
      : !body.trim() ? 'Write the message' : null;

  // An example of what the importer reads: a header naming the columns (any order).
  const downloadExample = () => {
    const blob = new Blob(['name,phone,email\nMariam Kintu,+256770123456,mariam@example.com\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'contacts-example.csv';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };
  // The file goes up and becomes a real list; it is then picked like any other.
  const uploadList = async (file) => {
    if (!file) return;
    setUpload((u) => ({ ...u, busy: true, error: '', result: null }));
    try {
      const list = await importContactListCsv(file, { listName: upload.name.trim() || file.name.replace(/\.csv$/i, ''), eventId: upload.eventId || undefined });
      const fresh = await loadContactLists();
      setLists(fresh);
      setListId(list.id);
      setUpload((u) => ({ ...u, busy: false, result: list }));
    } catch (e) {
      setUpload((u) => ({ ...u, busy: false, error: e?.message || 'Upload failed' }));
    }
  };

  const send = async () => {
    if (submitting) return;
    setSubmitting(true);
    try {
      // Persist the campaign to the backend as a DRAFT. Executing it (the real
      // blast to live contacts) is a deliberate, separate action — we never
      // auto-send to real people from here.
      const created = await createCampaign({
        name: name.trim(),
        channel, // backend enum: email | whatsapp | sms | push | billboard
        ...(campaignEventId ? { eventId: campaignEventId, scope: 'event' } : { scope: 'global' }),
        // Email: the template IS the message; the subject is recorded for the lists, sent from the template.
        ...(channel === 'email' && mailTpl ? {
          templateName: mailTpl.name,
          subject: mailTpl.subject,
          ...(fromName.trim() ? { emailSettings: { fromName: fromName.trim() } } : {}),
        } : {}),
        content: channel === 'sms' ? (body || undefined) : undefined,
        // The three columns the blast executor actually reads for WhatsApp. Sending them is
        // what makes the campaign send the message the operator composed.
        ...(channel === 'whatsapp' && !isDirect && waTemplate ? {
          templateName: waTemplate.name,
          templateLanguage: waTemplate.languageCode,
          templateComponents: waTemplate.components || [],
        } : {}),
        // A written message: the backend derives its WhatsApp template name from the words, saves it
        // to Designs when asked (a branch when the words changed from `branchOf`), and refuses it for
        // audiences with no transaction.
        ...(isDirect ? { whatsapp: {
          mode: 'direct',
          body: direct.body,
          buttonText: direct.withButton ? direct.buttonText : null,
          save: !!direct.save,
          label: direct.save ? direct.label.trim() : null,
          branchOf: direct.startedFrom || null,
        } } : {}),
        // REAL audienceSources the backend resolver targets at execute time (event
        // buyers/subscribers or a subscriber tag) — not a fabricated size.
        audience: { audienceSources: audience.sources, _totalResolved: audienceSize, label: audience.label, source: audienceSource },
        // The batch size is saved even when left at the audience's default, so every surface that
        // says what a click sends (the list's confirm, the drawer) reads the one number the send uses.
        ...((batchSize || resolved.detail?.batchSize || includeRecent) ? { metadata: {
          ...((batchSize || resolved.detail?.batchSize) ? { batchSize: batchSize || resolved.detail.batchSize } : {}),
          ...(includeRecent ? { enableBlastDedup: false } : {}),
        } } : {}),
      });
      const newCampaign = {
        id: created?.id || ('CMP-' + new Date().getFullYear()),
        name: created?.name || name.trim(),
        channel: channelLabel,
        audience: audience.label,
        audienceSize,
        batchSize: batchSize || resolved.detail?.batchSize || null,
        remaining: null,
        sent: 0, opened: 0, clicks: 0,
        status: 'Draft',
        when: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) + ' · ' + new Date().toTimeString().slice(0, 5),
        by: 'You',
        raw: created,
      };
      onSend(newCampaign);
      logAudit({ action: 'Campaign created', target: name.trim(), detail: `${channelLabel} · ${audienceSize.toLocaleString()} recipients · draft` });
      toast('Campaign created', 'success', `${name.trim()} · saved as draft · execute it to send the blast`);
    } catch (err) {
      toast('Campaign not created', 'error', err?.message || 'Backend rejected the request');
    } finally {
      setSubmitting(false);
    }
  };

  const STEPS = CAMPAIGN_STEPS;

  return (
    <Modal open={true} onClose={onClose} width="max-w-[820px]" title="New campaign" sub="Send a broadcast to a list of people. The blast goes out immediately once you send.">
      <div className="flex items-center gap-0 mb-4 border-b border-[#EEEDEA] -mx-5 px-5 pb-0">
        {STEPS.map(s => (
          <div key={s.id} className={`px-3 py-2 text-[11.5px] font-medium border-b-2 ${step === s.id ? 'border-[#1F2937] text-[#1F2937]' : 'border-transparent text-[#9CA3AF]'}`}>{s.label}</div>
        ))}
      </div>

      {step === 'channel' && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            {[
              { k: 'email',    l: 'Email',    d: 'An HTML email from Campaign emails', i: Mail },
              { k: 'whatsapp', l: 'WhatsApp', d: 'High open rates, fastest delivery in Uganda',     i: MessageSquare },
              { k: 'sms',      l: 'SMS',      d: 'Plain text, 160 chars, universal reach',          i: Hash },
            ].map(opt => (
              <button key={opt.k} onClick={() => setChannel(opt.k)} className={`text-left bg-white border rounded-md p-3 transition-colors ${channel === opt.k ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:border-[#D8D5CF]'}`}>
                <div className="w-8 h-8 rounded-md bg-[#F2F1ED] flex items-center justify-center mb-2"><opt.i className="w-4 h-4 text-[#4B5563]" strokeWidth={1.75} /></div>
                <div className="text-[12.5px] font-semibold text-[#1F2937]">{opt.l}</div>
                <div className="text-[10.5px] text-[#6B7280] mt-0.5 leading-snug">{opt.d}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 'audience' && (
        <div className="space-y-3">
          {/* Source picker */}
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: 'event',   l: 'Event audience', d: 'Buyers or subscribers of an event', i: CalendarDays },
              { k: 'segment', l: 'Saved segment',  d: 'A subscriber tag segment',          i: Tag },
              { k: 'list',    l: 'Contact list',   d: 'A list you brought — or upload one', i: Upload },
            ].map(opt => (
              <button key={opt.k} onClick={() => setAudienceSource(opt.k)} className={`text-left bg-white border rounded-md p-2.5 transition-colors ${audienceSource === opt.k ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:border-[#D8D5CF]'}`}>
                <div className="flex items-center gap-2 mb-1"><opt.i className="w-3.5 h-3.5 text-[#4B5563]" strokeWidth={1.75} /><div className="text-[12px] font-semibold text-[#1F2937]">{opt.l}</div></div>
                <div className="text-[10.5px] text-[#6B7280] leading-snug">{opt.d}</div>
              </button>
            ))}
          </div>

          {/* — EVENT AUDIENCE (real) — */}
          {audienceSource === 'event' && (
            <div className="space-y-2.5">
              <Field label="Event" required>
                <EventSelect value={eventId} current={picked[eventId]} onChange={(id, e) => { pick(e); setEventId(id); }} placeholder="— Pick an event —" className="w-full" />
              </Field>
              <div>
                <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Who to reach</div>
                <div className={`grid gap-2 ${channel === 'whatsapp' ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  {[
                    { k: 'buyers', l: 'Buyers', d: 'People who bought tickets' },
                    { k: 'subscribers', l: 'Subscribers', d: 'Followers / waitlist' },
                    { k: 'all', l: 'Everyone', d: 'Buyers + subscribers' },
                    // Reachable only by the phone they checked out with, so offered for WhatsApp alone.
                    ...(channel === 'whatsapp' ? [{ k: 'unfinished', l: 'Unfinished checkouts', d: 'Started buying, got no tickets' }] : []),
                  ].map(o => (
                    <button key={o.k} onClick={() => setAudienceType(o.k)} className={`text-left border rounded-md p-2.5 ${audienceType === o.k ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                      <div className="text-[12px] font-semibold text-[#1F2937]">{o.l}</div>
                      <div className="text-[10px] text-[#6B7280]">{o.d}</div>
                    </button>
                  ))}
                </div>
              </div>
              {audienceType === 'unfinished' && eventId && (
                <UnfinishedCheckoutsPicker
                  windowValue={unfinishedWindow}
                  onWindowChange={setUnfinishedWindow}
                  outcomes={unfinishedOutcomes}
                  onOutcomesChange={setUnfinishedOutcomes}
                  detail={resolved.detail}
                  loading={resolved.status === 'loading'}
                />
              )}
              {eventId && resolved.detail?.excluded
                ? <AudienceBreakdown resolved={resolved} channel={channel} includeRecent={includeRecent} onIncludeRecent={setIncludeRecent} />
                : <AudienceSizeBar resolved={resolved} idle={!eventId} idleHint="Pick an event to resolve the real audience size" />}
            </div>
          )}

          {/* — SAVED SEGMENT = real subscriber tags — */}
          {audienceSource === 'segment' && (
            <div className="space-y-2">
              {tags.length === 0 ? (
                <div className="text-center py-8 text-[12px] text-[#9CA3AF] bg-[#FBFAF7] border border-[#E6E4E1] rounded-md">No subscriber tags yet. Tags applied to subscribers become reusable segments here.</div>
              ) : (
                <div className="space-y-1.5 max-h-[300px] overflow-y-auto pr-1">
                  {tags.map(t => (
                    <label key={t.name} className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded border cursor-pointer ${selectedTag === t.name ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                      <div className="flex items-center gap-2.5 min-w-0"><input type="radio" name="tag" checked={selectedTag === t.name} onChange={() => setSelectedTag(t.name)} /><span className="text-[12.5px] font-medium text-[#1F2937] inline-flex items-center gap-1.5"><Tag className="w-3 h-3 text-[#9CA3AF]" />{t.name}</span></div>
                      <span className="text-[11px] font-mono tabular-nums text-[#6B7280]">{Number(t.count).toLocaleString()} subscribers</span>
                    </label>
                  ))}
                </div>
              )}
              {selectedTag && <AudienceSizeBar resolved={resolved} idle={false} idleHint="" />}
            </div>
          )}


          {/* — CONTACT LIST: an existing one, or a CSV that becomes one — */}
          {audienceSource === 'list' && (
            <div className="space-y-3">
              {lists.length > 0 && (
                <div className="space-y-1.5 max-h-[220px] overflow-y-auto pr-1">
                  {lists.map((l) => (
                    <label key={l.id} className={`flex items-center justify-between gap-2 px-3 py-2.5 rounded border cursor-pointer ${listId === l.id ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]'}`}>
                      <div className="flex items-center gap-2.5 min-w-0"><input type="radio" name="list" checked={listId === l.id} onChange={() => setListId(l.id)} />
                        <span className="text-[12.5px] font-medium text-[#1F2937] truncate">{l.name}</span>
                        {picked[l.eventId] && <span className="text-[10.5px] text-[#9CA3AF] truncate">· {evName(l.eventId)}</span>}</div>
                      <span className="text-[11px] font-mono tabular-nums text-[#6B7280] shrink-0">{Number(l.contactCount || 0).toLocaleString()} contacts</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-3 space-y-2">
                <div className="text-[11.5px] text-[#1F2937] font-medium">Upload a list</div>
                <div className="text-[10.5px] text-[#6B7280]">A CSV with a header naming its columns — <span className="font-mono">phone</span>, and <span className="font-mono">name</span> / <span className="font-mono">email</span> if you have them; or just numbers, one per line. <button onClick={downloadExample} className="underline inline-flex items-center gap-0.5"><Download className="w-3 h-3" />example</button></div>
                <div className="grid grid-cols-2 gap-2">
                  <Input value={upload.name} onChange={(e) => setUpload((u) => ({ ...u, name: e.target.value }))} placeholder="List name, e.g. Gabzy fans 2023" />
                  <EventSelect value={upload.eventId} current={picked[upload.eventId]} onChange={(id, e) => { pick(e); setUpload((u) => ({ ...u, eventId: id })); }} placeholder="These contacts are for… (event)" className="w-full" />
                </div>
                <label className={`h-8 px-3 text-[12px] font-medium rounded-md text-white inline-flex items-center gap-1.5 cursor-pointer ${upload.busy ? 'bg-[#9CA3AF]' : 'bg-[#1F2937] hover:bg-[#111827]'}`}>
                  {upload.busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}{upload.busy ? 'Uploading…' : 'Choose CSV and upload'}
                  <input type="file" accept=".csv,text/csv" disabled={upload.busy} onChange={(e) => { uploadList(e.target.files?.[0]); e.target.value = ''; }} className="hidden" />
                </label>
                {upload.error && <div className="text-[11.5px] text-[#9B1F1F]">{upload.error}</div>}
                {upload.result && (
                  <div className="text-[11.5px] text-[#1F2937]">
                    <b>{upload.result.name}</b> saved — {Number(upload.result.contactCount).toLocaleString()} people{upload.result.withNames ? `, ${Number(upload.result.withNames).toLocaleString()} with names` : ''}.
                    {upload.result.duplicatesDropped > 0 && <> {upload.result.duplicatesDropped.toLocaleString()} repeated numbers counted once.</>}
                    {upload.result.unreadable > 0 && <span className="text-[#8A5A12]"> {upload.result.unreadable.toLocaleString()} rows had no number we could read (e.g. {(upload.result.unreadableExamples || []).slice(0, 3).join(', ')}).</span>}
                  </div>
                )}
              </div>
              {listId && (resolved.detail?.excluded ? <AudienceBreakdown resolved={resolved} channel={channel} includeRecent={includeRecent} onIncludeRecent={setIncludeRecent} /> : <AudienceSizeBar resolved={resolved} idle={false} idleHint="" />)}
              <div className="bg-[#FEF7E6] border border-[#FDE68A] rounded-md p-2.5 text-[10.5px] text-[#8A5A12]">
                Only message people who agreed to hear from you. Anyone on the list who has told us to stop is left out automatically.
              </div>
            </div>
          )}
        </div>
      )}

      {step === 'compose' && (
        <div className="space-y-3">
          <Field label="Campaign name" required sub="Internal name · not seen by recipients"><Input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. AFCON Qualifier · 24h reminder" /></Field>
          <Field label="For event" sub={`The event this message is about — its name and links in the message, its organiser as the sender, and what an unsubscribe applies to.${audienceEventId ? ` The audience's event (${evName(audienceEventId)}) unless you pick another.` : ''}`}>
            <EventSelect value={campaignEventId} current={picked[campaignEventId]} onChange={(id, e) => { pick(e); setForEventId(id === audienceEventId ? '' : id); }} placeholder="— No event —" className="w-full" />
          </Field>
          {channel === 'email' && (
            <>
              <Field label="Email" required sub={<>The email itself — subject and HTML — is a template under <a className="underline" href="/design?tab=campaign-emails" target="_blank" rel="noreferrer">Design &amp; Comms → Campaign emails</a>, where you add or change one.</>}>
                {mailTpls.state === 'error' ? <div className="text-[12px] text-[#991B1B]">{mailTpls.error}</div> : (
                  <Select value={mailTpl?.name || ''} onChange={(e) => pickMailTemplate(e.target.value)} disabled={mailTpls.state !== 'ready'}
                    className="w-full">
                    <option value="">{mailTpls.state === 'ready' ? '— Pick the email to send —' : 'Loading templates…'}</option>
                    {mailTpls.templates.map((t) => <option key={t.name} value={t.name}>{t.name} — {t.subject}</option>)}
                  </Select>
                )}
              </Field>
              <Field label="From" optional sub={mailTpls.sender ? `The sender name in the inbox. Sent from ${mailTpls.sender.mailbox} — this platform's mailbox, or the organiser's own brand's if it has one.` : 'The sender name in the inbox.'}>
                <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder={mailTpls.sender?.fromName ? `${mailTpls.sender.fromName} — or e.g. B'raka Land EA` : "e.g. B'raka Land EA"} />
              </Field>
              {mailTpl && !mailTpl.loading && (
                <TemplateCheck check={checkMailTemplate(mailTpl, mailTpls.variables)} variables={mailTpls.variables} needsEvent={!!campaignEventId} />
              )}
            </>
          )}
          {channel === 'whatsapp' ? (
            /* A WhatsApp blast names an APPROVED TEMPLATE. This step used to be a free-text
               body — which Meta rejects for anyone outside the 24-hour window (i.e. an entire
               marketing list), and which the blast executor never read anyway: it sent
               `campaign.templateName || 'welcome_to_conference_2'`, a template used once in
               the platform's history. The operator saw the words they wrote and the audience
               would have received a stranger's conference welcome. */
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                {[
                  { k: 'direct', l: 'Write a message', d: 'No template needed — about the person’s own order (WhatsApp Direct Send, utility)', disabled: !directAllowed },
                  { k: 'template', l: 'Approved template', d: 'Any audience, marketing included — the wording is the template Meta approved' },
                ].map((o) => (
                  <button key={o.k} disabled={o.disabled} onClick={() => setWaMode(o.k)}
                    title={o.disabled ? 'A written message must be about the recipient’s own transaction, so it can go to buyers, unfinished checkouts or guests — not subscribers, segments or uploads.' : undefined}
                    className={`text-left border rounded-md p-2.5 ${o.disabled ? 'opacity-50 cursor-not-allowed border-[#E6E4E1]' : (waMode === o.k ? 'border-[#1F2937] bg-[#FBFAF7]' : 'border-[#E6E4E1] hover:bg-[#FBFAF7]')}`}>
                    <div className="text-[12px] font-semibold text-[#1F2937]">{o.l}</div>
                    <div className="text-[10.5px] text-[#6B7280] leading-snug">{o.disabled ? 'Only for buyers, unfinished checkouts and guests' : o.d}</div>
                  </button>
                ))}
              </div>
              {isDirect ? (
                <DirectSendComposer value={direct} onChange={setDirect} detail={resolved.detail} />
              ) : (
                <Field label="WhatsApp template" required sub="Meta only accepts an approved template for a business-initiated message — free text is delivered only to someone who messaged you in the last 24 hours, which a marketing list is not.">
                  <WhatsAppTemplatePicker value={waTemplate} onChange={setWaTemplate} variables />
                </Field>
              )}
            </div>
          ) : channel === 'sms' && (
            <>
              <Field label="Message body" required sub="Max 160 characters · plain text">
                <Textarea rows={3} value={body} onChange={e => setBody(e.target.value)} maxLength={160} placeholder="TKT: Your tickets for {eventName} on {date} are ready. Open: tkt.ug/t/{ticketRef}" />
              </Field>
              <div className="text-[10.5px] text-[#9CA3AF] tabular-nums">{body.length} / 160 chars</div>
            </>
          )}
        </div>
      )}

      {step === 'review' && (
        <div className="space-y-3">
          <div className="bg-[#FBFAF7] border border-[#E6E4E1] rounded-md p-4">
            <dl className="grid grid-cols-[120px_1fr] gap-y-2 text-[12px]">
              <dt className="text-[#6B7280]">Channel</dt><dd className="text-[#1F2937]">{channelLabel}</dd>
              <dt className="text-[#6B7280]">Audience</dt><dd className="text-[#1F2937]">{audience?.label} <span className="text-[#9CA3AF]">· {countNoun(audienceSize, 'person', 'people')} · source: {audienceSource}</span></dd>
              <dt className="text-[#6B7280]">Campaign</dt><dd className="text-[#1F2937]">{name}</dd>
              <dt className="text-[#6B7280]">For event</dt><dd className="text-[#1F2937]">{campaignEventId ? evName(campaignEventId) : <span className="text-[#9CA3AF]">none</span>}</dd>
              {resolved.detail?.batchSize && <>
                <dt className="text-[#6B7280] pt-1">People per batch</dt>
                <dd className="text-[#1F2937] flex items-center gap-2">
                  <input type="number" min={1} value={batchSize ?? resolved.detail.batchSize}
                    onChange={(e) => { const n = Math.floor(Number(e.target.value)); setBatchSize(n > 0 ? n : null); }}
                    className="w-[90px] h-7 bg-white border border-[#E6E4E1] rounded px-2 text-[12px] tabular-nums" />
                  <span className="text-[#6B7280] text-[11.5px]">{(resolved.detail.batches || 1) <= 1 ? 'everyone in one batch' : `${resolved.detail.batches} batches — one click each`}</span>
                </dd>
              </>}
              {channel === 'email' && <>
                <dt className="text-[#6B7280]">Email</dt><dd className="text-[#1F2937]"><span className="font-mono">{mailTpl?.name}</span></dd>
                <dt className="text-[#6B7280]">Subject</dt><dd className="text-[#1F2937]">{fillSample(mailTpl?.subject, emailSample)}</dd>
                <dt className="text-[#6B7280]">From</dt><dd className="text-[#1F2937]">{`${fromName.trim() || mailTpls.sender?.fromName || ''} <${mailTpls.sender?.mailbox || '—'}>`.trim()}</dd>
              </>}
              {channel === 'whatsapp' && !isDirect && <><dt className="text-[#6B7280]">Template</dt><dd className="text-[#1F2937] font-mono">{waTemplate?.name} <span className="text-[#9CA3AF] font-sans">· {waTemplate?.languageCode}</span></dd></>}
              {isDirect && <>
                <dt className="text-[#6B7280]">Sent as</dt><dd className="text-[#1F2937]">A written message — WhatsApp Direct Send, utility</dd>
                <dt className="text-[#6B7280]">Saved</dt><dd className="text-[#1F2937]">{direct.save ? <>Yes, as “{direct.label}” — reusable from <b>Start from</b> next time</> : 'No — a one-off'}</dd>
                <dt className="text-[#6B7280]">Delivery</dt><dd className="text-[#1F2937]">Dropped if it cannot be delivered within 12 hours — a late reminder is worse than none</dd>
              </>}
            </dl>
          </div>
          {isDirect ? (
            <div className="grid grid-cols-[300px_1fr] gap-4 items-start">
              <WhatsAppBubble
                text={renderWithSample(direct.body, resolved.detail?.sample)}
                buttonText={direct.withButton ? direct.buttonText : null}
                link={resolved.detail?.sample?.link}
                caption={resolved.detail?.sample ? <>As <b>{resolved.detail.sample.firstName || 'this person'}</b> ({resolved.detail.sample.phone}) will see it.</> : null}
              />
              <DirectSendTest
                eventId={eventId}
                content={directContent}
                sample={resolved.detail?.sample}
                save={!!direct.save}
                label={direct.label}
              />
            </div>
          ) : channel === 'email' && mailTpl ? (
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Preview · as {resolved.detail?.sample?.firstName ? <>{emailSample.firstName}, the first person in this audience</> : <>{SAMPLE_RECIPIENT.firstName}, a stand-in recipient</>}</div>
              <MailPreview subject={fillSample(mailTpl.subject, emailSample)} html={fillSample(mailTpl.html, emailSample)} />
              <div className="mt-3"><EmailTest templateName={mailTpl.name} fromName={fromName.trim()} eventId={campaignEventId} /></div>
            </div>
          ) : (
            <div className="bg-white border border-[#E6E4E1] rounded-md p-4">
              <div className="text-[10.5px] uppercase tracking-[0.06em] font-semibold text-[#6B7280] mb-1.5">Preview · {channelLabel}</div>
              {channel === 'whatsapp' ? (
                /* Show the PARAMETERS that will actually go out. The approved body copy lives
                   with Meta, not here, so this deliberately shows what we control and says so
                   — rather than rendering `body`, which for WhatsApp is now never sent. */
                <div className="space-y-1.5">
                  {templateEditableFields(waTemplate?.components).map((f) => (
                    <div key={f.path.join('.')} className="flex gap-2 text-[12px]">
                      <span className="text-[#9CA3AF] capitalize min-w-[120px]">{f.label}</span>
                      <span className="text-[#374151] break-all">{f.value || <span className="text-[#9CA3AF]">empty</span>}</span>
                    </div>
                  ))}
                  {templateEditableFields(waTemplate?.components).length === 0 && (
                    <div className="text-[12px] text-[#9CA3AF]">No fillable parameters — sends exactly as Meta approved it.</div>
                  )}
                  <div className="text-[10.5px] text-[#9CA3AF] pt-1.5">The message wording itself is the approved <span className="font-mono">{waTemplate?.name}</span> template held by Meta; only these values are set here.</div>
                </div>
              ) : (
                <div className="text-[12px] text-[#374151] whitespace-pre-wrap">{body}</div>
              )}
            </div>
          )}
          {/* What the button below actually does. This said "Pressing Send blast … runs immediately and
              cannot be undone" over a button labelled Create campaign that only saves a draft. */}
          <div className="bg-[#FEF7E6] border border-[#FDE68A] rounded-md p-3 text-[11.5px] text-[#8A5A12]">
            <span className="font-semibold">Create campaign</span> saves this as a draft for <span className="font-semibold">{audienceSize.toLocaleString()}</span> {audienceSize === 1 ? 'person' : 'people'} — nothing is sent yet.
            {(resolved.detail?.batches || 1) > 1
              ? <> <span className="font-semibold">Send now</span> on it in the campaign list sends the first <span className="font-semibold">{Math.min(audienceSize, batchSize ?? resolved.detail.batchSize).toLocaleString()}</span>; each next batch is its own click. A sent message cannot be undone.</>
              : <> It goes out when you press <span className="font-semibold">Send now</span> on it in the campaign list; that cannot be undone.</>}
          </div>
        </div>
      )}

      <div className="px-5 py-3 border-t border-[#EEEDEA] bg-[#FBFAF7] -mx-5 -mb-4 mt-4 sticky -bottom-4 z-10 flex items-center justify-between">
        {step !== 'channel' ? (
          <button onClick={() => setStep(step === 'audience' ? 'channel' : step === 'compose' ? 'audience' : 'compose')} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED] inline-flex items-center gap-1.5"><ChevronLeft className="w-3.5 h-3.5" />Back</button>
        ) : <div />}
        <div className="flex items-center gap-2">
          {step === 'compose' && !canReview && reviewReason && <span className="text-[11.5px] text-[#8A5A12]">{reviewReason}</span>}
          <button onClick={onClose} className="h-8 px-3 text-[12px] font-medium rounded-md text-[#1F2937] hover:bg-[#F2F1ED]">Cancel</button>
          {step === 'channel'  && <button onClick={() => setStep('audience')} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] text-white">Continue</button>}
          {step === 'audience' && <button onClick={() => setStep('compose')} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] text-white">Continue</button>}
          {step === 'compose'  && <button onClick={() => setStep('review')} disabled={!canReview} title={!canReview && reviewReason ? reviewReason : undefined} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white">Review &amp; send</button>}
          {step === 'review'   && <button onClick={send} disabled={submitting} className="h-8 px-3 text-[12px] font-medium rounded-md bg-[#1F2937] hover:bg-[#111827] disabled:bg-[#9CA3AF] text-white inline-flex items-center gap-1.5"><Send className="w-3.5 h-3.5" />{submitting ? 'Creating…' : 'Create campaign'}</button>}
        </div>
      </div>
    </Modal>
  );
};

/* ============================================================================
   PAGE: DESIGN & COMMS — mirrors the reference screenshot
   ============================================================================ */

